import { Material } from '../../../../classes/material.class';
import { Planning } from '../../../../classes/planning.class';
import { getMaterialConsumptionValue, getMaterialProperty } from '../../../../pipes/material-property.pipe';

export type MarkingKind = 'streep' | 'figuratie';

// Gedeeld met need-item.component.ts (zijbalk-badge) en behoefte-overview.component.ts (filter).
export const MARKING_KIND_LABELS: Record<MarkingKind, string> = {
    streep: 'strepen',
    figuratie: 'figuratie',
};

// Genormaliseerde material_type.name-waarden die als "markering" gelden (Thermoplast/Verf/Koudplast).
// De echte namen in de catalogus wisselen in hoofdlettergebruik (bv. 'Thermoplast' vs 'verf').
const MARKING_MATERIAL_TYPES = ['thermoplast', 'verf', 'koudplast'] as const;
type MarkingMaterialType = (typeof MARKING_MATERIAL_TYPES)[number];

const MARKINGS_ENTITY_TYPE_KEY = 'markings';

// Verbruik <= 7kg/m2 is een lengtemarkering (streep), > 7kg/m2 (doorgaans 10kg/m2) is figuratie.
// Bekende beperking: de "Brite Line" Thermoplast-varianten hebben voor beide 7kg/m2 en worden dus
// niet onderscheiden - geaccepteerd, zie plan.
const STREEP_FIGURATIE_THRESHOLD = 8.5;

export const BEHOEFTE_COLUMNS: { key: string; label: string }[] = [
    { key: 'thermoplast-streep', label: 'Thermoplast (strepen)' },
    { key: 'thermoplast-figuratie', label: 'Thermoplast (figuratie)' },
    { key: 'verf-streep', label: 'Verf (strepen)' },
    { key: 'verf-figuratie', label: 'Verf (figuratie)' },
    { key: 'koudplast-streep', label: 'Koudplast (strepen)' },
    { key: 'koudplast-figuratie', label: 'Koudplast (figuratie)' },
    { key: 'markings-unknown', label: 'Markeren (materiaal onbekend)' },
    { key: 'blast', label: 'Stralen' },
    { key: 'wipe', label: 'Vegen' },
    { key: 'inventorize', label: 'Inventariseren' },
    { key: 'workplace', label: 'Werkplaats' },
];

function getMaterialMarkingType(material?: Material | null): MarkingMaterialType | null {
    const type = getMaterialProperty(material, 'type').toLowerCase();

    return (MARKING_MATERIAL_TYPES as readonly string[]).includes(type) ? (type as MarkingMaterialType) : null;
}

export function getMaterialMarkingKind(material?: Material | null): MarkingKind | null {
    if (!getMaterialMarkingType(material)) {
        return null;
    }

    const consumption = getMaterialConsumptionValue(material);

    if (consumption == null) {
        return null;
    }

    return consumption <= STREEP_FIGURATIE_THRESHOLD ? 'streep' : 'figuratie';
}

function getNeedMaterials(need: Planning, materialsById: Map<number, Material>): Material[] {
    return (need.planning_materials ?? []).map(pm => materialsById?.get(pm.material_id)).filter(Boolean);
}

// Kolom(men) waar een behoefte in thuishoort. Voor markeringen kan een behoefte in meerdere
// kolommen tegelijk vallen als er meerdere kwalificerende materialen aan gekoppeld zijn.
export function getNeedColumnKeys(need: Planning, materialsById: Map<number, Material>): string[] {
    const entitytypeKey = need.mainPlanning?.entitytype_key;

    if (entitytypeKey !== MARKINGS_ENTITY_TYPE_KEY) {
        return entitytypeKey ? [entitytypeKey] : [];
    }

    const keys = new Set<string>();

    for (const material of getNeedMaterials(need, materialsById)) {
        const materialType = getMaterialMarkingType(material);
        const markingKind = getMaterialMarkingKind(material);

        if (materialType && markingKind) {
            keys.add(`${materialType}-${markingKind}`);
        }
    }

    return keys.size ? [...keys] : ['markings-unknown'];
}

// Of een kolom getoond moet worden gegeven de actieve materiaaltype-/streep-figuratie-filters.
// Werkplek-achtige kolommen (blast/wipe/inventorize/workplace, geen '-' in de key) zijn daar niet
// door geraakt.
export function columnMatchesFilters(columnKey: string, materialTypes: string[], markingKinds: string[]): boolean {
    if (columnKey === 'markings-unknown') {
        return markingKinds.includes('onbekend');
    }

    const [materialType, markingKind] = columnKey.split('-');

    if (!markingKind) {
        return true;
    }

    return materialTypes.includes(materialType) && markingKinds.includes(markingKind);
}
